import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Button, Card, CircularProgress, FormControl, InputLabel, MenuItem, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BackButton from "../BackButton/BackButton";
import CustomSelect from "../forms/theme-elements/CustomSelect";
import { CreateScrapyReport, PageSpeedCreate } from "../../hooks/scrapyData";
import { usePopup } from "../../context/commonPop";
import { popupType } from "../../common/constant";
import ComfirmationPopup from "../../common/comfirmationPopup";
import TechnicalSeoTools from "../ai/TechnicalSeoTools";
import AiToolsQuickLinks from "../ai/AiToolsQuickLinks";

const CardWrapper = styled(Card)(({ theme }) => ({
  padding: "16px 16px 16px 16px",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none !important",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "15px",
  "@media screen and (max-width:600px)": {
    flexDirection: "column",
    alignItems: "stretch",
  },
  "& .InfoBtnWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    "@media screen and (max-width:600px)": {
      flexDirection: "column-reverse",
      alignItems: "stretch",
      gap: "15px",
    },
    "& .DomainInfoWrapper": {
      "& h5": {
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: "15px",
        lineHeight: "1",
        "@media screen and (max-width:600px)": {
          textAlign: "center",
        },
      },
      "& a": {
        color: theme.palette.primary.main,
        fontWeight: 600,
        fontSize: "13px",
        lineHeight: "1",
        display: "block",
        marginTop: "4px",
        "@media screen and (max-width:600px)": {
          textAlign: "center",
        },
        "& svg": {
          width: "15px",
          height: "12px",
          color: theme.palette.primary.main,
          lineHeight: "1",
        },
      },
    },
  },
  "& .BtnAndDatepickerWrapper": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    "@media screen and (max-width:600px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "15px",
    },
    "& button": {
      textTransform: "none",
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      borderRadius: "5px",
      boxShadow: "none",
      padding: "6px 22px",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,

        color: theme.palette.common.white,
      },
      "@media screen and (max-width:600px)": {
        width: "100%",
      },
      "&:focus": {
        outline: "0",
      },
    },
    "& .DatepickerWrapper": {
      "& .MuiInputBase-root": {
        "& .MuiSelect-select": {
          padding: "8px 40px 8px 22px",
        },
      },
    },
  },
}));

export function formatDate(inputDate) {
  const date = new Date(inputDate);

  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "short" });
  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;

  const formattedDate = `${day} ${month} ${year} ${formattedHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;

  return formattedDate;
}

function InnerHeader({ listData, setReportTime }) {
  const { allSession, website, isCrawlProgress, projectName } = (Array.isArray(listData) && listData.length && listData[0]) || {};
  const domain = website?.replace(/^https?:\/\//i, "").split("/")[0];
  const [open, setOpen] = useState(false);
  const [seoToolsOpen, setSeoToolsOpen] = useState(false);
  const [value, setValue] = useState("");
  const [loader, setLoader] = useState(false);
  const { createScrapy } = CreateScrapyReport();
  const { CreatePageSpeed } = PageSpeedCreate();
  const { customPopupModel } = usePopup();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const handleChange = event => {
    setValue(event.target.value);
    const selectedItem = allSession.find(item => item.sessionId === event.target.value) || { sessionId: "", createAt: "" };
    // Format and store the date in another state
    const newFormattedDate = formatDate(selectedItem.createAt);
    setReportTime(newFormattedDate);
    const newUrl = `/project-detail/${event.target.value}?show=${searchParams.get("show")}&origin=${searchParams.get("origin")}`;

    navigate(newUrl);
  };

  useEffect(() => {
    if (params.sessionId) {
      setValue(params.sessionId);
    }
  }, [params.sessionId]);

  useEffect(() => {
    if (Array.isArray(allSession) && allSession.length) {
      const firstReportTime = allSession?.[0]?.createAt;
      const firstReportTimeInFormat = formatDate(firstReportTime);
      setReportTime(firstReportTimeInFormat);
    }
  }, [allSession]);

  const handleClick = async () => {
    setLoader(true);
    const item = {
      website,
      id: searchParams.get("origin"),
    };
    try {
      const scrapydata = await createScrapy(item);
      if (scrapydata && scrapydata.data && scrapydata.data.ScrapUrl && scrapydata.data.ScrapUrl.sessionId) {
        const { sessionId, authorId } = scrapydata.data.ScrapUrl;
        customPopupModel({
          type: popupType.success,
          contentFunc: <Typography component="p">Scraping is in progress. Results will be on the dashboard shortly.</Typography>,
        });
        await CreatePageSpeed(authorId, searchParams.get("origin"), website, sessionId);
        setLoader(false);
      } else if (scrapydata && scrapydata.errors) {
        customPopupModel({
          type: popupType.failed,
          contentFunc: <Typography component="p">{scrapydata.errors || "something went wrong"}</Typography>,
        });
        setLoader(false);
      } else {
        customPopupModel({
          type: popupType.failed,
          contentFunc: <Typography component="p">Your crawl limit is reached.</Typography>,
        });
        setLoader(false);
      }
    } catch (error) {
      console.error("Error during scraping:", error);
      customPopupModel({
        type: popupType.failed,
        contentFunc: <Typography component="p">An error occurred during scraping.</Typography>,
      });
      setLoader(false);
    }
  };
  const handleNewCrawlBtn = () => {
    setLoader(true);
    setOpen(true);
  };
  const handleClosePopup = () => {
    setOpen(false);
    setLoader(false);
  };
  return (
    <>
      <CardWrapper>
        <Box className="InfoBtnWrapper">
          <BackButton sx={{ "& button": { marginBottom: "0 !important" } }} />
          {website && domain && projectName && (
            <Box className="DomainInfoWrapper">
              <Typography component="h5">{projectName}</Typography>
              <Typography component="a" href={website} target="_blank" rel="noopener noreferrer">
                {domain}
                <OpenInNewIcon />
              </Typography>
            </Box>
          )}
        </Box>
        <Box className="BtnAndDatepickerWrapper">
          <AiToolsQuickLinks projectId={searchParams.get("origin")} />
          <Button onClick={() => setSeoToolsOpen(true)}>Technical SEO</Button>
          <Button onClick={() => navigate(`/crawl-progress/${params.sessionId}`, { state: { projectName } })}>Crawl report</Button>
          <Button disabled={!website || isCrawlProgress || loader} onClick={handleNewCrawlBtn}>
            {loader ? <CircularProgress size={24.5} /> : "New Crawl"}
          </Button>
          <FormControl className="DatepickerWrapper">
            <InputLabel id="demo-simple-select-label">Select Report Date</InputLabel>
            <CustomSelect labelId="demo-simple-select-label" label="Select Report Date" id="month-dd" size="small" onChange={handleChange} value={value}>
              {Array.isArray(allSession) &&
                allSession.length > 0 &&
                allSession.map(item => (
                  <MenuItem className="menuitemlist" value={item?.sessionId} key={item.sessionId}>
                    {formatDate(item?.createAt)}
                  </MenuItem>
                ))}
            </CustomSelect>
          </FormControl>
        </Box>
      </CardWrapper>
      <ComfirmationPopup open={open} handleClosePopup={handleClosePopup} msg="Are you sure you want to generate a new report?" handleFunc={handleClick} sucessBtnName="Genrate" />
      <TechnicalSeoTools open={seoToolsOpen} onClose={() => setSeoToolsOpen(false)} sessionId={params.sessionId} />
    </>
  );
}

export default InnerHeader;
